import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import clsx from 'clsx';
import _fetchApi from '../fetch';
import { parseToken } from '../global';

const sessionKey = 'itmo_oauth';

// ITMO ID sign-in isn't live yet: until then every ITMO button is shown disabled with a "coming soon" caption
const ITMO_READY = false;

function base64url(bytes) {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function startITMOLogin(connect = false) {
  const state = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
  const response = await _fetchApi('/oauth/itmo/start?' + new URLSearchParams({state, code_challenge: challenge}));
  const result = await response.json();
  if (!response.ok || !result.url) {
    throw new Error(response.status === 503 ? 'Вход через ITMO ID пока не настроен' : 'Не удалось открыть ITMO ID');
  }
  const token = Cookies.getJSON('user_data')?.token;
  const uid = token ? parseToken(token).uid : null;
  if (connect && !uid) throw new Error('Сначала войдите в Sort Me');
  sessionStorage.setItem(sessionKey, JSON.stringify({
    state, verifier, connect, uid, createdAt: Date.now(),
    returnTo: window.location.pathname + window.location.search + window.location.hash,
  }));
  window.location.assign(result.url);
}

export async function finishITMOLogin(search) {
  const pending = JSON.parse(sessionStorage.getItem(sessionKey) || 'null');
  sessionStorage.removeItem(sessionKey);
  const params = new URLSearchParams(search);
  if (!pending || !pending.state || pending.state !== params.get('state') || !pending.verifier ||
      !pending.createdAt || Date.now() - pending.createdAt > 10 * 60 * 1000) {
    throw new Error('Попытка входа устарела. Начните вход заново.');
  }
  if (params.has('error')) throw new Error('Вход через ITMO ID отменён или отклонён.');
  const code = params.get('code');
  if (!code) throw new Error('ITMO ID не вернул код авторизации.');
  const token = Cookies.getJSON('user_data')?.token;
  const uid = token ? parseToken(token).uid : null;
  if (uid !== pending.uid || (pending.connect && !uid)) {
    throw new Error('Аккаунт Sort Me изменился. Начните привязку заново.');
  }
  const response = await _fetchApi((pending.connect ? '/oauth/connect' : '/oauth') + '?provider=itmo', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(pending.connect ? {Authorization: 'Bearer ' + token} : {}),
    },
    body: JSON.stringify({code, code_verifier: pending.verifier}),
  });
  const result = await response.json();
  if (!response.ok || result.error) {
    throw new Error(response.status === 422 ? 'Этот аккаунт ITMO ID уже привязан к Sort Me.' : 'Не удалось завершить вход через ITMO ID. Попробуйте ещё раз.');
  }
  if (!pending.connect) {
    const parsed = parseToken(result.token);
    Cookies.set('user_data', {token: result.token, login: result.user.login, uid: parsed.uid}, {
      expires: new Date(parsed.exp * 1000), path: '/', sameSite: 'lax', secure: window.location.protocol === 'https:',
    });
  }
  const returnTo = new URL(pending.returnTo || '/profile', window.location.origin);
  return returnTo.origin === window.location.origin ? returnTo.pathname + returnTo.search + returnTo.hash : '/profile';
}

export function ITMOButton({connect = false, className, disabled, children, setLogining}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  if (!ITMO_READY) {
    return <button type="button" className={clsx(className, 'itmo-soon')} disabled>
      {children || 'Войти через ITMO ID'}
      <span className="itmo-soon-caption">Скоро сделаем!</span>
    </button>;
  }
  return <button type="button" className={className} disabled={disabled || loading} onClick={async () => {
    setLoading(true);
    setLogining?.(true);
    setError('');
    try {
      await startITMOLogin(connect);
    } catch (error) {
      setError(error.message);
      setLoading(false);
      setLogining?.(false);
    }
  }}>{loading ? 'Переходим в ITMO ID...' : error || children || 'Войти через ITMO ID'}</button>;
}

export function ITMOCallback() {
  const [error, setError] = useState('');
  useEffect(() => {
    const search = window.location.search;
    window.history.replaceState(null, '', window.location.pathname);
    finishITMOLogin(search).then(path => window.location.replace(path)).catch(error => setError(error.message));
  }, []);
  return <div className="main-content">
    <h2 className="header">Вход через ITMO ID</h2>
    <p role={error ? 'alert' : 'status'}>{error || 'Завершаем вход...'}</p>
    {error && <Link to="/profile" className="itmo-callback-link">Вернуться в Sort Me</Link>}
  </div>;
}
